import ScrollTrigger from 'gsap/dist/ScrollTrigger';
import { sbEditable } from '@storyblok/storyblok-editable';
import classNames from 'classnames';
import type { ReactElement, RefObject, ReactNode } from 'react';
import { useCallback, useImperativeHandle, useRef, useState } from 'react';
import styles from './PardotPdfForm.module.scss';
import { setupTransitionInTimeline } from './PardotPdfForm.transitions';
import type { PardotPdfFormProps } from './PardotPdfForm.data';
import { useScrollTransition } from 'util/react-transition-component';
import Heading from '../../atoms/heading/Heading';
import Text from '../../atoms/text/Text';
import InputField from '../../atoms/input-field/InputField';
import type { FieldValues } from 'react-hook-form';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import PrimaryButton from '../../atoms/primary-button/PrimaryButton';
import type { PardoSubmitPdfFormData } from '../../../util/api';
import { pardotApi } from '../../../util/api';
import { useGlobalStore } from '../../../stores/global';
import { useScrollProviderContext } from '../../unlisted/ScrollProvider';
import type { ScrollProviderContextType } from '../../unlisted/ScrollProvider';
import useUpdateEffect from '../../../hooks/useUpdateEffect';
import { NODE_PARAGRAPH, render } from 'storyblok-rich-text-react-renderer';
import Icon from '../../atoms/icon/Icon';
import CountryDropdown from '../../molecules/country-dropdown/CountryDropdown';
import RegionDropdown from '../../molecules/region-dropdown/RegionDropdown';
import type { BooleanSchema, StringSchema } from 'yup';
import ParallaxCardHover from '../../atoms/parallax-card-hover/ParallaxCardHover';
import useWindowSize from '../../../hooks/useWindowSize';
import { useThemeColorProviderContext } from '../../unlisted/ThemeColorProvider';
import type { ThemeColorProviderContextType } from '../../unlisted/ThemeColorProvider';
import type { CtaRefType } from '../../../util/cta/cta.data';
import CustomDropdown from '../../molecules/custom-dropdown/CustomDropdown';

export enum FormState {
  Default,
  SubmitSuccess,
  SubmitError,
}

export default function PardotPdfForm({
  blok,
  parentHandleSubmit,
  layout = 'standalone',
  ref,
  ...props
}: PardotPdfFormProps): ReactElement {
  const localRef = useRef(null);
  const elementRef = localRef as RefObject<HTMLDivElement | null>;
  const globalStore = useGlobalStore();
  const isSubmitting = useRef(false);
  const [formLayoutState, setFormLayoutState] = useState(FormState.Default);
  const buttonRef = useRef<CtaRefType>(null);

  const { width: windowWidth } = useWindowSize();
  const { isDarkTheme } = useThemeColorProviderContext() as ThemeColorProviderContextType;

  const validationRules: Record<string, StringSchema | BooleanSchema> = {};

  blok.pardot_input_fields?.forEach((field) => {
    let yupString = Yup.string();
    if (field.required) {
      yupString = yupString.required(`${field.label} is required`);
    }
    if (field.type === 'email') {
      yupString = yupString.email('Email is invalid');
    }
    validationRules[field.pardot_key] = yupString;
  });

  blok.pardot_checkboxes?.forEach((checkbox) => {
    if (checkbox.required) {
      validationRules[checkbox.id] = Yup.bool().oneOf([true], checkbox.error_message);
    }
  });

  const validationSchema = Yup.object().shape(validationRules);

  useScrollTransition({
    scroll: {
      trigger: elementRef,
      vars: {},
    },
    setupOptions: () => ({
      refs: {
        elementRef,
      },
      setupTransitionInTimeline,
    }),
  });

  const scrollContext = useScrollProviderContext() as ScrollProviderContextType;
  const formOptions = { resolver: yupResolver(validationSchema) };
  const { register, handleSubmit, formState } = useForm(formOptions);
  const { errors } = formState;

  useUpdateEffect(() => {
    ScrollTrigger.refresh();
    if (parentHandleSubmit) {
      scrollContext.scrollTo(0, 0, 600);
    } else {
      const rect = elementRef.current?.getBoundingClientRect();
      const currentY = scrollContext.scrollStatusRef.current?.offset.y || 0;
      scrollContext.scrollTo(0, (rect?.top || 0) + currentY - 200, 600);
    }
  }, [formLayoutState]);

  const onValidationPassed = useCallback(
    async (data: FieldValues | PardoSubmitPdfFormData) => {
      if (!isSubmitting.current) {
        isSubmitting.current = true;
        try {
          const response = await pardotApi.submitPdfForm(
            blok.pardot_id,
            data as PardoSubmitPdfFormData,
          );
          // Success
          if (response.data.success) {
            if (blok.linkedin_tracking_id) {
              // @ts-ignore
              window.lintrk('track', { conversion_id: blok.linkedin_tracking_id });
            }
            setFormLayoutState(FormState.SubmitSuccess);
            parentHandleSubmit?.(FormState.SubmitSuccess);
          } else {
            // Mismatch on fields/validation from Pardot
            setFormLayoutState(FormState.SubmitError);
            parentHandleSubmit?.(FormState.SubmitError);
          }
          // @ts-ignore
        } catch ({ error }) {
          // Show general (server)error-message (something went wrong, please try again later).
          setFormLayoutState(FormState.SubmitError);
          parentHandleSubmit?.(FormState.SubmitError);
        }
        isSubmitting.current = false;
      }
    },
    [blok.pardot_id, blok.linkedin_tracking_id, parentHandleSubmit],
  );

  const cardHeight = 600;
  const cardWidth = 450;

  const isDark = (isDarkTheme && !blok.use_accent_color) || (!isDarkTheme && blok.use_accent_color);
  const isLight =
    (!isDarkTheme && !blok.use_accent_color) || (isDarkTheme && blok.use_accent_color);

  // const accentColor = {
  //   foreground: isDark ? 'rgb(256,256,256)' : 'rgb(0,0,0)',
  //   background: isDark ? 'rgb(0,0,0)' : 'rgb(244,244,244)',
  // };
  //
  // useThemeScrollBlock(elementRef, blok.use_accent_color, accentColor, true);

  useImperativeHandle(
    ref,
    () => ({
      formLayoutState,
    }),
    [formLayoutState],
  );

  return (
    <div
      ref={elementRef}
      className={classNames(styles.pardotPdfForm, props.className, {
        [styles.darkTheme]: isDark,
        [styles.lightTheme]: isLight,
        [styles.embedded]: layout === 'embedded',
      })}
      {...sbEditable(blok)}
      data-scroll-to="download-pdf"
      style={
        {
          '--height': `min(${cardHeight}px, ${((windowWidth - 40) * 4) / 3}px)`,
          '--width': `min(${cardWidth}px, ${windowWidth - 40}px)`,
        } as React.CSSProperties
      }
    >
      <div className={classNames(styles.container, 'grid-gutter max-content-width')}>
        <form onSubmit={handleSubmit(onValidationPassed)} className={classNames(styles.form)}>
          <div className={classNames(styles.header)}>
            {formLayoutState === FormState.Default && (
              <>
                {layout !== 'embedded' && blok.imageFront?.filename && (
                  <ParallaxCardHover
                    imgSrcBack={blok.imageBack?.filename}
                    imgSrcFront={blok.imageFront?.filename}
                    width="450px"
                    height="600px"
                    style={{
                      margin: 'auto',
                      marginBottom: 80,
                    }}
                  />
                )}
                <Heading
                  transitionInType="splitWordsSlideInUp"
                  className={classNames(styles.title)}
                  as="h3"
                  size="heading-03"
                  disableTriggerInOnScroll={false}
                >
                  {blok.title}
                </Heading>
                {render(blok.description, {
                  nodeResolvers: {
                    [NODE_PARAGRAPH]: (children: ReactNode) => (
                      <Text
                        transitionInType="splitWordsSlideInUp"
                        className={classNames(styles.description)}
                        as="p"
                        size="body-standard"
                        disableTriggerInOnScroll={false}
                      >
                        {children}
                      </Text>
                    ),
                  },
                })}
              </>
            )}
            <div className={styles.center}>
              {formLayoutState === FormState.SubmitSuccess && (
                <>
                  <Heading
                    transitionInType="splitWordsSlideInUp"
                    className={classNames(styles.title)}
                    as="h3"
                    size="heading-03"
                    disableTriggerInOnScroll={false}
                  >
                    {blok.success_title}
                  </Heading>
                  {render(blok.success_description, {
                    nodeResolvers: {
                      [NODE_PARAGRAPH]: (children: ReactNode) => (
                        <Text
                          transitionInType="splitWordsSlideInUp"
                          className={classNames(styles.description)}
                          as="p"
                          size="body-standard"
                          disableTriggerInOnScroll={false}
                        >
                          {children}
                        </Text>
                      ),
                    },
                  })}
                </>
              )}
              {formLayoutState === FormState.SubmitError && (
                <>
                  <Heading
                    transitionInType="splitWordsSlideInUp"
                    className={classNames(styles.title)}
                    as="h3"
                    size="heading-03"
                    disableTriggerInOnScroll={false}
                  >
                    Apologies
                  </Heading>
                  <Text
                    transitionInType="splitWordsSlideInUp"
                    className={classNames(styles.description)}
                    as="p"
                    size="body-standard"
                    disableTriggerInOnScroll={false}
                  >
                    Something went wrong, please try again later.
                  </Text>
                </>
              )}
            </div>
          </div>

          <div className={styles.center}>
            {formLayoutState === FormState.Default && (
              <>
                <fieldset className={classNames(styles.fieldset)}>
                  {blok.pardot_input_fields?.map((field, index) => {
                    let Autocomplete;
                    if (field.type === 'country') Autocomplete = CountryDropdown;
                    if (field.type === 'region') Autocomplete = RegionDropdown;
                    if ((field.dropdown_options?.length || 0) > 0) Autocomplete = CustomDropdown;
                    return (
                      <InputField
                        key={field.pardot_key}
                        register={register}
                        error={errors[field.pardot_key]}
                        className={classNames(styles.inputField)}
                        type={
                          field.type === 'country' || field.type === 'region'
                            ? 'text'
                            : field.type || 'text'
                        }
                        name={field.pardot_key}
                        label={field.label}
                        hidden={field.hidden}
                        placeholder={field.placeholder}
                        required={field.required}
                        options={field.dropdown_options}
                        index={index}
                        // @ts-ignore // todo make a check, if dropdown_options exist then Autocomplete options is not undefined but i dont know how to do that
                        Autocomplete={Autocomplete}
                      />
                    );
                  })}

                  {blok.pardot_checkboxes?.map((checkbox) => (
                    <div
                      className={classNames(styles.checkboxContainer, styles.inputContainer)}
                      key={checkbox.id}
                    >
                      <input type="checkbox" id={checkbox.id} {...register(checkbox.id)} />
                      <div className={styles.labelContainer}>
                        {render(checkbox.label, {
                          nodeResolvers: {
                            [NODE_PARAGRAPH]: (children: ReactNode) => (
                              <Text
                                as="label"
                                size="body-standard"
                                transitionInType="none"
                                htmlFor={checkbox.id}
                              >
                                {children} {checkbox.required ? '*' : ''}
                              </Text>
                            ),
                          },
                        })}
                      </div>
                      {errors?.[checkbox.id]?.message && (
                        <div className={classNames(styles.errorContainer)}>
                          <Icon className={classNames(styles.icon)} name="error" />
                          <Text
                            as="span"
                            size="foot-note"
                            className={classNames(styles.errorMessage)}
                            transitionInType="none"
                          >
                            <>{errors[checkbox.id]?.message}</>
                          </Text>
                        </div>
                      )}
                    </div>
                  ))}
                </fieldset>

                <div className={classNames(styles.submitHolder)}>
                  <PrimaryButton
                    ref={buttonRef}
                    className={classNames(styles.button)}
                    label={blok.button_label || globalStore.globalSettings.form_submit}
                    type="submit"
                    disableTriggerInOnScroll={false}
                  />
                </div>

                {render(blok.disclaimer, {
                  nodeResolvers: {
                    [NODE_PARAGRAPH]: (children: ReactNode) => (
                      <Text
                        transitionInType="none"
                        className={classNames(styles.disclaimer)}
                        as="p"
                        size="foot-note"
                        disableTriggerInOnScroll={false}
                      >
                        {children}
                      </Text>
                    ),
                  },
                })}
              </>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}
